import * as React from "react";
import { toast } from "react-toastify";

import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import Table, { ITableInstance } from "src/library/components/Tables/Table";
import DropInfoSidebar from "src/core/drops/components/DropInfoSidebar";
import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { useDrop } from "src/core/drops/hooks";
import { PageProps } from "src/pages/Router/routes";
import {
  IUser,
  useUserApi
} from "src/core/users/api/users.api";
import { usersTableColumns } from "src/core/shared/components/tables";
import ActionButton, { IAction } from "src/library/components/Button/ActionButton";
import { Modal } from "src/library/components/Modal";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { LikeToDo } from "src/library/components/LikeToDo";
import { Button } from "src/library/components/Button";

interface UrlParams {
  dropId: string;
  customerId: string;
}

const DropUsersPage: React.FunctionComponent<PageProps<unknown, UrlParams>> = props => {
  const {
    history, terminology, match
  } = props;

  const TableRef = React.useRef<ITableInstance<IUser>>(null);
  const dropId = match ? parseInt(match.params.dropId, 0) : 0;
  const customerId = match ? parseInt(match.params.customerId, 0) : 0;
  const [ { isLoading: isLoadingDrop, drop } ] = useDrop(dropId);
  const [ usersResponse, getUsers ] = useUserApi("LIST_BRANCH_USERS");
  const [ suspendUserResponse, suspendUser ] = useUserApi("UPDATE_USER");
  const [ resendInviteResponse, resendInvite ] = useUserApi("RESEND_INVITE");
  const [ deleteUserResponse, deleteUser ] = useUserApi("DELETE_USER");
  const [ deleteUserModalData, setDeleteUserModalData ] = React.useState<{userId: number} | null>(null);
  const checkPermission = useCheckUserPermission();

  const onFetchData = React.useCallback((filters: FilterParams<IUser>, pagination: PaginatedParams<IUser>) => {
    getUsers({
      pagination,
      filters: {
        ...filters,
        customerId: { $not: null }
      },
      dropId
    });
  }, []);

  // on suspend update
  React.useEffect(() => {
    if (suspendUserResponse.data) {
      toast.success(`The ${terminology.user.singular.text} has been successfully updated.`);

      if (TableRef.current) {
        TableRef.current.reloadTable(true);
      }
    }

    if (suspendUserResponse.error) {
      toast.error(`Could not update ${terminology.user.singular.text} - ${suspendUserResponse.error.message}`);
    }
  }, [ suspendUserResponse, terminology.user.singular.text ]);

  // on resend invite
  React.useEffect(() => {
    if (resendInviteResponse.data) {
      toast.success("The invite has been sent");
    }

    if (resendInviteResponse.error) {
      toast.error(`Invite not sent ${terminology.user.singular.text} - ${resendInviteResponse.error.message}`);
    }
  }, [ resendInviteResponse, terminology.user.singular.text ]);

  // On user deletion
  React.useEffect(() => {
    if (deleteUserResponse.data) {
      toast.success(`The ${terminology.user.singular.text} has been successfully deleted.`);

      if (TableRef.current) {
        TableRef.current.reloadTable(true);
      }
    }

    if (deleteUserResponse.error) {
      toast.error(`Could not delete ${terminology.user.singular.text} - ${deleteUserResponse.error.message}`);
    }
  }, [ deleteUserResponse, terminology.user.singular.text ]);

  const createNewUser = React.useCallback(() => {
    const link = (
      <Button
        onClick={() => {
          history.push(`/admin/customers/${customerId}/users/create/`, { dropId });
        }}
      >
        {`Create new ${terminology.user.singular.text}`}
      </Button>
    );

    const buttons = [ link ] as JSX.Element[];

    return buttons;
  }, [
    customerId,
    dropId,
    history,
    terminology.user.singular.text
  ]);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        <LikeToDo
          copy="Setup a new user for this delivery point"
          buttons={createNewUser()}
        />

        <Table
          tableId={`customerId:${customerId}`}
          ref={TableRef}
          mainAction={row => history.push(`/admin/customers/${customerId}/users/${row.original.id}/details`)}
          error={usersResponse.error}
          tableOf={terminology.user.plural.text}
          data={usersResponse.data ? usersResponse.data.result : []}
          loading={usersResponse.pending}
          onFetchData={onFetchData}
          totalRows={usersResponse.data ? usersResponse.data.count : 0}
          pages={usersResponse.data ? usersResponse.data.pages : 0}
          columns={usersTableColumns({
            ActionButton: row => {
              const items: IAction[] = [
                {
                  title: "Edit",
                  disabled: !checkPermission("amend-user"),
                  action: () => {
                    history.push(`/admin/customers/${customerId}/users/${row.original.id}/details`);
                  }
                },
                {
                  title: `${row.original.status === "active" ? "Suspend" : "Activate"}`,
                  disabled: !checkPermission("amend-user"),
                  action: () => {
                    const updatedUser = {
                      ...row.original,
                      status: row.original.status === "active" ? "suspended" : "active"
                    };

                    suspendUser(updatedUser);
                  }
                },
                {
                  title: "Delete",
                  disabled: !checkPermission("delete-user"),
                  action: () => {
                    setDeleteUserModalData({ userId: row.original.id });
                  }
                }
              ];

              if (row.original.lastLoggedIn === null) {
                items.push({
                  disabled: !checkPermission("create-user"),
                  title: "Resend invite",
                  action: () => resendInvite({ id: row.original.id })
                });
              }

              return (
                <ActionButton
                  title="Actions"
                  items={items}
                />
              );
            }
          })}
        />
      </MainContentWrapper>

      <InfoBarWrapper>
        <DropInfoSidebar
          terminology={terminology}
          isLoading={isLoadingDrop}
          data={drop}
        />
      </InfoBarWrapper>

      <Modal
        small
        show={deleteUserModalData !== null}
        type="confirm"
        title="Are you sure?"
        body="Deleting this user cannot be undone."
        backgroundAction={() => setDeleteUserModalData(null)}
        actions={[
          {
            name: "No, cancel",
            action: () => setDeleteUserModalData(null),
            $buttonType: "secondary"
          },
          {
            name: "I confirm",
            $buttonType: "primary",
            action: () => {
              if (deleteUserModalData) {
                deleteUser({ id: deleteUserModalData.userId });
              }
            }
          }
        ]}
      />
    </Layout>
  );
};

export default DropUsersPage;
